import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mountain } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      nav("/");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1747854805840-9be7d5e360e6" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="wordmark text-5xl mb-3">Bhoomi<span className="wordmark-dot" style={{ background: "#F0B429" }}></span><span className="wordmark-mitra" style={{ color: "#F0B429" }}>Mitra</span></div>
          <h2 className="text-3xl font-bold tracking-tight leading-tight">Turn your property into ready cash — trusted, transparent, direct.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 space-y-5">
          <div>
            <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground grid place-items-center mb-3">
              <Mountain size={18} />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{t.auth.registerTitle}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t.auth.registerSub}</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>{t.auth.name}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="reg-name" />
            </div>
            <div>
              <Label>{t.auth.email}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="reg-email" />
            </div>
            <div>
              <Label>{t.auth.phone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="reg-phone" />
            </div>
            <div>
              <Label>{t.auth.password}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1.5" data-testid="reg-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="reg-submit">
              {loading ? t.common.loading : t.auth.registerBtn}
            </Button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{t.auth.or}</span></div>
          </div>
          <Button type="button" variant="outline" onClick={google} className="w-full" data-testid="reg-google">
            <svg width="16" height="16" viewBox="0 0 24 24" className="mr-2"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.99.66-2.25 1.05-3.72 1.05-2.87 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.09a6.62 6.62 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.07l3.66 2.84C6.71 7.31 9.13 5.38 12 5.38Z"/></svg>
            {t.auth.googleBtn}
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            <Link to="/login" className="text-primary underline">{t.auth.switchToLogin}</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
